import React, { useContext } from 'react';
import type { EffectRangeAll, ScrollEffectParams } from '@wix/effects-kit';
import {
  Composites,
  CustomScroll,
  Divider,
  ToggleSwitch,
} from '@wix/wix-base-ui';
import styles from '../assets/styles.scss';
import type { AnimationScrollCustomizeProps } from '../types';
import { TranslationContext } from '../context/TranslationContext';

import { DropDownList } from '../components/AnimationCustomizeComponents';
import { AnimationCustomizeControls } from '../components/AnimationCustomizeControls';

/**
 * Automatically create customize controls from schema
 */
export const AnimationScrollCustomize: React.FC<
  AnimationScrollCustomizeProps
> = ({
  trigger,
  effectName,
  rangeSchema,
  schema,
  values,
  onChange = () => {},
  isScrollPreview,
  onPreview,
  onPreviewStop,
}) => {
  const { translate } = useContext(TranslationContext);

  const range = (values.range || rangeSchema.enum![0]) as EffectRangeAll;
  const hasTracks = rangeSchema.enum!.length > 1;

  React.useEffect(() => {
    onPreview(trigger, effectName, values);
    return () => {
      onPreviewStop(trigger);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScrollPreview = (checked: boolean) => {
    if (checked) {
      onPreview(trigger, effectName, values);
    } else {
      onPreviewStop(trigger);
    }
  };

  return (
    <div className={styles.animationCustomize}>
      <CustomScroll allowOuterScroll>
        <Composites.ToggleSwitch>
          <ToggleSwitch
            value={!!isScrollPreview}
            infoIconProps={{
              text: translate('tooltip_preview_scroll'),
              shouldTranslate: false,
            }}
            label={translate('label_preview_scroll')}
            onChange={onScrollPreview}
            shouldTranslate={false}
          />
        </Composites.ToggleSwitch>
        <Divider long />
        {hasTracks ? (
          <DropDownList
            name="range"
            params={rangeSchema}
            value={(values as ScrollEffectParams).range}
            onChange={(name, newValue) => onChange(effectName, name, newValue)}
          />
        ) : null}
        <AnimationCustomizeControls
          effectName={effectName}
          schema={schema[range]!}
          values={values}
          onChange={onChange}
          divider={hasTracks}
        />
      </CustomScroll>
    </div>
  );
};
